import { t, Trans } from '@lingui/macro';
import { useMutation } from '@tanstack/react-query';
import { Button, ProductPrice } from '@we-make-websites/ui-lib';
import clsx from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useContext } from 'use-context-selector';
import { localeToStore, storeLocale } from '@/root/constants';
import Overlay from 'components/overlay/Overlay';
import { OverlayContext } from 'components/overlay/OverlayContext';
import { ProductContext } from 'components/productContext/ProductProvider';
import type { StoreMediaImage } from 'components/productContext/types/storeMedia';
import { FormCheckboxField, FormField } from 'components/signUpForm/FormFields';

import styles from './BackInStockModal.module.scss';

interface BISFormInput {
  email: string;
  acceptMarketing: boolean;
}

const BackInStockModal = () => {
  const router = useRouter();
  const locale = storeLocale(router.locale || router.defaultLocale);
  const store = localeToStore(locale);
  const shopifyDomain = `${store}.myshopify.com`;

  const { close } = useContext(OverlayContext);
  const productContext = useContext(ProductContext);
  if (productContext === undefined)
    throw new Error('BackInStockModal must be used within a ProductContext');

  const {
    productType,
    title,
    price,
    media,
    proOnly,
    id,
    selectedVariant,
    proCustomer,
  } = productContext;

  /**
   * If product or selected variant is pro only and logged in customer is not a pro
   */
  const proOnlyHide = (selectedVariant?.proOnly || proOnly) && !proCustomer;

  /**
   * Only show price if product has a price and pro only hide is false
   */
  const showPrice = price && !proOnlyHide;

  const productId = id.split('/').pop();
  const variantId = selectedVariant?.id.split('/').pop();

  const [error, setError] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BISFormInput>();

  // Get first image.
  const featuredMedia = media.find(
    (image) => image.type === 'IMAGE'
  ) as StoreMediaImage;

  const {
    mutate,
    isSuccess,
    isError,
    isLoading: loading,
  } = useMutation({
    mutationFn: (data: BISFormInput) => {
      const { email, acceptMarketing } = data;

      return fetch(
        `https://app.backinstock.org/stock_notification/create.json?shop=${shopifyDomain}&notification[email]=${email}&notification[product_no]=${productId}&notification[quantity_required]=1&notification[accepts_marketing]=${acceptMarketing}&variant[variant_no]=${variantId}`
      )
        .then((response) => {
          if (response.ok) {
            return response.json();
          }

          // throw error when request failed.
          const errorMsg = t({
            id: 'backInStock.errorMessage',
            message: 'Registering Back in stock notification failed',
          });
          throw new Error(errorMsg);
        })
        .then(({ errors, status }) => {
          if (status !== 'Error') return;

          let errorMsg = '';

          for (const errorType in errors) {
            // collect all the error messages into a string
            const errorLine = Array.isArray(errors[errorType])
              ? errors[errorType].join()
              : errors[errorType];

            errorMsg += `${errorLine} <br />`;
          }

          // throw error when request failed.
          throw new Error(errorMsg);
        });
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  const onSubmit: SubmitHandler<BISFormInput> = (data) => {
    // Handle BIS form submission.
    mutate(data);
  };

  return (
    <Overlay
      type="mobile-drawer"
      blockClass="storeSelectorOverlay"
      direction="left"
      namespace="backInStock"
    >
      <div className={styles.backInStock}>
        <h2
          className={clsx(
            styles.backInStock__title,
            'text-heading-h5-mobile text-heading-h5-desktop'
          )}
        >
          <Trans id="backInStock.heading">Notify me when back in stock</Trans>
        </h2>

        <div className={styles.backInStock__product}>
          {featuredMedia?.image && (
            <div className={styles['backInStock__productImage']}>
              <Image
                src={featuredMedia.image.url}
                sizes="(min-width: 1024px) 511px, (min-width: 576px) 800px, 300px"
                fill
                alt={featuredMedia.image.altText}
                title={featuredMedia.image.altText}
              />
            </div>
          )}

          <div className={styles.backInStock__productContent}>
            {productType && (
              <p
                className={clsx(
                  'text-utility-utility-small-mobile',
                  'text-utility-utility-desktop'
                )}
              >
                {productType}
              </p>
            )}

            {title && (
              <p
                className={clsx(
                  'text-body-1-bold-mobile',
                  'text-body-1-bold-desktop'
                )}
              >
                {title}
              </p>
            )}

            {showPrice && (
              <div className={styles.backInStock__productPriceWrapper}>
                <ProductPrice
                  {...{
                    price: selectedVariant?.price || price,
                    isLoading: false,
                  }}
                  type="unit"
                  className={clsx(
                    styles.backInStock__productPrice,
                    'text-body-2-bold-desktop text-body-2-bold-mobile',
                    selectedVariant?.compareAtPrice &&
                      styles['backInStock__productPrice--sale']
                  )}
                />
                {selectedVariant?.compareAtPrice && (
                  <ProductPrice
                    {...{
                      price: selectedVariant?.compareAtPrice,
                      isLoading: false,
                    }}
                    type="compare"
                    className={clsx(
                      styles.backInStock__productPrice,
                      'text-body-2-bold-desktop text-body-2-bold-mobile'
                    )}
                  />
                )}

                {selectedVariant?.bundlePrice && (
                  <ProductPrice
                    {...{
                      price: selectedVariant.bundlePrice,
                      isLoading: false,
                    }}
                    type="bundle"
                    className={clsx(
                      styles.backInStock__productPrice,
                      styles['backInStock__productPrice--bundle'],
                      'text-body-small-desktop text-body-small-mobile'
                    )}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {!isSuccess && (
          <div className={styles.backInStock__formWrapper}>
            <p
              className={clsx(
                styles.backInStock__copy,
                'text-body-2-mobile text-body-2-desktop'
              )}
            >
              <Trans id="backInStock.copy">
                Get notified when back in stock. Enter your email and hit ‘Sign
                up’.
              </Trans>
            </p>

            <form
              className={styles.backInStock__form}
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <div className="form-group">
                <FormField
                  type="email"
                  id="loginEmail"
                  error={errors.email}
                  loading={loading}
                  label={t({
                    id: 'backInStock.emailPlaceholder',
                    message: 'Email Address',
                  })}
                  register={() =>
                    register('email', {
                      required: t({
                        id: 'backInStock.emailRequired',
                        message: 'Email is required',
                      }),
                    })
                  }
                />
              </div>

              <div
                className={clsx(
                  styles.backInStock__marketingWrapper,
                  'text-body-small-desktop text-body-small-mobile'
                )}
              >
                <FormCheckboxField
                  id="backInStockAcceptMarketing"
                  label={t({
                    id: 'backInStock.acceptMarketing',
                    message: 'Yes, send me a notification',
                  })}
                  loading={loading}
                  register={() => register('acceptMarketing')}
                  className={styles.backInStock__marketing}
                />

                <a
                  target="_blank"
                  href="https://www.wellacompany.com/privacy-policy"
                  rel="noreferrer"
                  aria-label={t({
                    id: 'backInStock.privacyPolicy',
                    message: 'Privacy Policy, opens a new tab',
                  })}
                >
                  Privacy Policy
                </a>
              </div>

              <div className={styles.backInStock__formFooter}>
                <Button type="submit" disabled={loading}>
                  <span className="text-utility-utility-mobile text-utility-utility-desktop">
                    <Trans id="backInStock.signUp">Sign up</Trans>
                  </span>
                </Button>

                <Button
                  type="button"
                  modifiers="text"
                  onClick={() => close('backInStock')}
                >
                  <span className="text-utility-utility-mobile text-utility-utility-desktop">
                    <Trans id="backInStock.cancel">Cancel</Trans>
                  </span>
                </Button>
              </div>
            </form>
          </div>
        )}

        {isSuccess && (
          <p className="text-body-1-mobile text-body-1-desktop">
            <Trans id="backInStock.successMessage">
              Thanks, your notification request has been received.
            </Trans>
          </p>
        )}

        {isError && (
          <p
            className={clsx(
              styles.backInStock__error,
              'text-body-1-mobile text-body-1-desktop'
            )}
            dangerouslySetInnerHTML={{ __html: error }}
          ></p>
        )}
      </div>
    </Overlay>
  );
};

export default BackInStockModal;
